<template>
    <div>
        <div @mousedown="closeEditor($event)">
            <div class="px-xl-5 px-4 editor-container">
                <div class="row">
                    <div class="col-12">
                        <br />
                        <br />
                        <div>
                            <button class="btn btn-link pl-0 py-1" @click="returnToEditor()">
                                <span class="icon icon-sm icon-glyphicons-basic-chevron-left align-middle mr-1"></span>
                                <span class="align-middle">Back to Screen</span>
                            </button>
                        </div>
                        <br />
                        <br />
                    </div>
                </div>
                <!-- Header Description & Buttons -->
                <div class="row mb-4">
                    <div class="col-12">
                        <div class="d-flex align-items-center mw-100">
                            <div class="flex-grow-1 mr-2 mr-md-5">
                                <p class="h6 font-weight-bolder">Configure Custom Form</p>
                                <p class="text-secondary mb-0">
                                    Create a form input, then configure its behaviour. Click on a form input to view its
                                    configuration.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Custom Form Editor -->
                <div class="row mb-4">
                    <div class="col-12">
                        <rich-text
                            id="custom-form-text"
                            v-model="formHtmlContent"
                            :allow-form-elements="true"
                            min-height="400px"
                            v-on:block-click="selectChoiceById($event, true, true)"
                            v-on:block-created="createChoice($event)"
                        >
                            <template slot="extra-toolbar-items">
                                <info-popover content="Toggle Sidebar Visibility" placement="top" class="ml-2">
                                    <button class="btn btn-sm btn-gray px-2" @click="toggleEditorDisplay()">
                                        <span class="icon icon-glyphicons-basic-sidebar-right"></span>
                                    </button>
                                </info-popover>
                                <button
                                    @click="savePanel()"
                                    :disabled="editorLoading || readOnly"
                                    class="btn btn-primary btn-sm ml-1"
                                >
                                    Save
                                    <span v-show="editorLoading" class="ml-2">
                                        <span
                                            class="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        <span class="sr-only">Loading...</span>
                                    </span>
                                </button>
                            </template>
                        </rich-text>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12">
                        <button
                            @click="savePanel()"
                            :disabled="editorLoading || readOnly"
                            class="btn btn-primary btn-sm"
                            id="save-btn"
                        >
                            Save
                            <span v-show="editorLoading" class="ml-2">
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                <span class="sr-only">Loading...</span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <!-- Editor Menu -->
            <div id="editor-menu" class="right-sidebar bg-white shadow" :class="{ 'd-none': !showEditor }">
                <div class="right-sidebar-contentflex-column">
                    <div class="right-sidebar-content-block">
                        <div class="d-flex align-items-start left-sidebar-content-block mb-3">
                            <div class="flex-grow-1 pr-2">
                                <p class="text-secondary mb-0 small">
                                    Click on an input to highlight it in your custom form. Customize each input's
                                    behaviour below.
                                </p>
                            </div>
                            <div class="flex-grow-0">
                                <div @click="closeEditor()">
                                    <button type="button" class="close" aria-label="Close">
                                        <span
                                            class="icon icon-glyphicons-basic-menu-close align-middle"
                                            style="font-size: 20px"
                                        ></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-for="(choice, index) in activeChoices" :key="choice.id">
                            <div
                                @click="selectChoiceFromMenu(choice, $event)"
                                :class="{
                                    'choice-focussed': choice.form_block_id === selectedChoice.form_block_id,
                                }"
                                class="sidebar-editor-block rounded-lg mb-4 bg-offwhite"
                                :id="'edit-choice' + choice.form_block_id"
                            >
                                <div class="card-body right-sidebar-card-body">
                                    <!-- Label -->
                                    <div class="mb-3">
                                        <div>
                                            <div class="mb-1 mb-2 d-flex">
                                                <div class="flex-grow-1">
                                                    <span class="pb-1 small font-weight-500">Reporting Label</span>
                                                    <help-text
                                                        content="A short label used to identify this input in reports."
                                                        class="ml-1"
                                                    ></help-text>
                                                </div>
                                                <div class="flex-grow-0 mr-1 small">
                                                    <span
                                                        v-show="choice.form_block_id === selectedChoice.form_block_id"
                                                        class="badge badge-primary"
                                                        >Selected</span
                                                    >
                                                </div>
                                            </div>
                                            <div>
                                                <input
                                                    v-model="choice.text"
                                                    placeholder="A short identifier for this input"
                                                    class="form-control form-control-sm"
                                                    maxlength="200"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Placeholder -->
                                    <div>
                                        <div class="mb-3">
                                            <div class="mb-1">
                                                <span class="pb-1 small font-weight-500">Placeholder</span>
                                                <help-text
                                                    content="A placeholder shown to learners when the input is blank."
                                                    class="ml-1"
                                                ></help-text>
                                            </div>
                                            <div>
                                                <input
                                                    v-model="choice.placeholder"
                                                    @input="updateChoicePlaceholder(choice)"
                                                    placeholder="A placeholder shown when this input is blank"
                                                    class="form-control form-control-sm"
                                                    maxlength="200"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Input Value Type -->
                                    <div class="d-flex mb-1">
                                        <div class="flex-grow-1 pr-1">
                                            <div class="pb-1 small font-weight-500">Input Type</div>
                                            <select
                                                v-model="choice.accepted_value_type"
                                                class="form-control form-control-sm"
                                                @change="updateChoiceValueType(choice)"
                                            >
                                                <option value="text">Text (all characters)</option>
                                                <option value="number">Numeric (numbers only)</option>
                                                <option value="select">Dropdown (select from a list)</option>
                                                <option value="textarea">Textarea (free text)</option>
                                            </select>
                                        </div>
                                        <div class="flex-grow-1 pl-1" v-show="choice.accepted_value_type != 'textarea'">
                                            <div class="pb-1 small font-weight-500">Score (if correct)</div>
                                            <input
                                                v-model="choice.score"
                                                class="form-control form-control-sm"
                                                type="number"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <div v-show="choice.accepted_value_type == 'select'" class="pt-1">
                                            <div class="d-flex">
                                                <div class="flex-grow-0 ml-2">
                                                    <span
                                                        class="icon icon-glyphicons-basic-step-forward icon-flipped text-secondary"
                                                    ></span>
                                                </div>
                                                <div class="flex-grow-1">
                                                    <div
                                                        v-for="(option, index) in choice.accepted_value_options"
                                                        :key="index"
                                                        class="mr-2 ml-1 value-option"
                                                    >
                                                        <span v-show="optionIsEdit(choice, index)" class="mr-1">
                                                            <input
                                                                type="text"
                                                                class="w-auto d-inline form-control form-control-sm"
                                                                placeholder="Option text"
                                                                v-model="choice.accepted_value_options[index]"
                                                                :id="'choice-' + choice.id + '-option-' + index"
                                                            />
                                                        </span>
                                                        <span
                                                            @click="editChoiceOption(index)"
                                                            role="button"
                                                            aria-label="Edit form input option"
                                                            class="align-middle ml-1 mr-2 medium"
                                                            v-show="!optionIsEdit(choice, index)"
                                                            >{{ option }}</span
                                                        >
                                                        <span
                                                            @click="closeOptionEdit()"
                                                            class="icon-btn align-middle text-primary mx-2"
                                                            role="button"
                                                            v-show="optionIsEdit(choice, index)"
                                                        >
                                                            <span
                                                                class="icon icon-sm icon-glyphicons-basic-check"
                                                            ></span>
                                                        </span>
                                                        <span
                                                            @click="removeChoiceOption(choice, index)"
                                                            class="icon-btn align-middle medium"
                                                            role="button"
                                                        >
                                                            <span
                                                                class="icon icon-100 icon-glyphicons-basic-bin"
                                                            ></span>
                                                        </span>
                                                    </div>
                                                    <button
                                                        @click="addChoiceOption(choice)"
                                                        class="btn btn-gray badge badge-gray ml-1"
                                                    >
                                                        + Add Option
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Correct Value & Score -->
                                    <div v-show="choice.accepted_value_type != 'textarea'">
                                        <div class="mb-3 pr-2">
                                            <div class="pb-1 small font-weight-500">Correct Value</div>
                                            <div class="d-flex align-items-center flex-wrap">
                                                <div
                                                    class="flex-grow-0 mr-3 mb-2"
                                                    v-for="(value, valueIndex) in choice.correct_values"
                                                    :key="valueIndex"
                                                >
                                                    <div class="d-flex align-items-center">
                                                        <div class="flex-grow-0" style="margin-right: 2px">
                                                            <input
                                                                type="text"
                                                                class="form-control form-control-sm correct-value-input"
                                                                placeholder="Value (e.g. John, 21)"
                                                                v-model="choice.correct_values[valueIndex]"
                                                            />
                                                        </div>
                                                        <div class="flex-grow-0">
                                                            <button
                                                                class="btn btn-icon btn-sm p-1"
                                                                @click="removeCorrectValueVariation(choice, valueIndex)"
                                                            >
                                                                <span
                                                                    class="icon icon-glyphicons-basic-bin align-middle"
                                                                ></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="flex-grow-0 mb-2">
                                                    <button
                                                        class="btn btn-gray badge badge-gray"
                                                        @click="addCorrectValueVariation(choice)"
                                                    >
                                                        <span class="icon icon-glyphicons-basic-plus"></span>
                                                        Add Variation
                                                    </button>
                                                </div>
                                            </div>
                                            <div class="small text-secondary">
                                                Tip: Enter an asterisk (*) to accept any non-blank input as correct.
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Feedback Message -->
                                    <div class="mb-3">
                                        <div class="pb-1 small font-weight-500">Feedback Message</div>
                                        <textarea
                                            v-model="choice.message"
                                            placeholder="Message content"
                                            rows="2"
                                            class="form-control form-control-sm mb-1"
                                            maxlength="2000"
                                        ></textarea>
                                        <div
                                            class="small text-secondary"
                                            v-show="choice.accepted_value_type != 'textarea'"
                                        >
                                            Shown only if the learner's entry is incorrect.
                                        </div>
                                    </div>
                                    <!-- Hint Message -->
                                    <div>
                                        <div>
                                            <div class="custom-control custom-checkbox mb-2">
                                                <input
                                                    v-model="choice.show_text_on_hover"
                                                    type="checkbox"
                                                    class="custom-control-input"
                                                    :id="'visible-marker' + choice.form_block_id"
                                                />
                                                <label
                                                    class="custom-control-label"
                                                    :for="'visible-marker' + choice.form_block_id"
                                                    >Show a hint message on hover</label
                                                >
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <div v-show="choice.show_text_on_hover">
                                                <textarea
                                                    v-model="choice.hint_message"
                                                    placeholder="Hint message"
                                                    rows="2"
                                                    class="form-control form-control-sm"
                                                    maxlength="2000"
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Input Width -->
                                    <div class="pb-1 small font-weight-500">Input Width</div>
                                    <div class="d-flex align-items-center">
                                        <div class="flex-grow-0 mr-3">
                                            <div class="d-flex align-items-center">
                                                <div class="radius-range pt-2 mr-3">
                                                    <input
                                                        v-model="choice.marker_width"
                                                        @change="updateChoiceWidth(choice)"
                                                        type="range"
                                                        step="5"
                                                        min="35"
                                                        max="600"
                                                        class="custom-range"
                                                    />
                                                </div>
                                                <input
                                                    class="form-control form-control-sm"
                                                    type="number"
                                                    v-model="choice.marker_width"
                                                    @change="updateChoiceWidth(choice)"
                                                    style="width: 60px"
                                                />
                                            </div>
                                        </div>
                                        <div class="flex-grow-1 d-flex justify-content-end align-items-center">
                                            <button
                                                @click.stop.prevent="shiftChoicePosition(index, -1)"
                                                class="btn btn-transparent p-1"
                                            >
                                                <span class="icon icon-100 icon-glyphicons-basic-arrow-up"></span>
                                            </button>
                                            <button
                                                @click.stop.prevent="shiftChoicePosition(index, 1)"
                                                class="btn btn-transparent p-1"
                                            >
                                                <span class="icon icon-100 icon-glyphicons-basic-arrow-down"></span>
                                            </button>
                                            <button
                                                class="btn btn-sm btn-transparent p-1"
                                                @click="removeChoice(choice)"
                                            >
                                                <span class="icon icon-glyphicons-basic-bin"></span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-show="panel.choices.length == 0">
                            <p class="text-center text-secondary my-4">No form inputs created yet.</p>
                        </div>
                        <div class="d-flex justify-content-end">
                            <info-popover content="Hide Editor Sidebar" placement="left" class="mr-2">
                                <button class="btn btn-sm btn-gray px-2" @click="toggleEditorDisplay()">
                                    <span class="icon icon-glyphicons-basic-sidebar-right"></span>
                                </button>
                            </info-popover>
                            <button
                                @click="savePanel()"
                                :disabled="editorLoading || readOnly"
                                class="btn btn-primary btn-sm"
                            >
                                Save
                                <span v-show="editorLoading" class="ml-2">
                                    <span
                                        class="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    <span class="sr-only">Loading...</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <global-style-block v-if="caseObj" :case-settings="caseObj"></global-style-block>
    </div>
</template>

<script>
import RichText from "@/components/RichText"
import GlobalStyleBlock from "@/components/GlobalStyleBlock"
import InfoPopover from "@/components/InfoPopover"
import HelpText from "@/components/HelpText"
import http from "@/axios.js"

export default {
    name: "CustomFormEditor",
    components: {
        GlobalStyleBlock,
        RichText,
        HelpText,
        InfoPopover,
    },
    data() {
        return {
            panel: {
                elements: [],
                choices: [],
            },
            selectedChoice: {
                elements: [],
            },
            showEditor: false,
            editorLoading: false,
            formHtmlContent: null,
            formIsInit: false,
            selectedOptionIndex: null,
            previousRoute: null,
        }
    },
    computed: {
        caseObj() {
            return this.$store.getters.case
        },
        caseID() {
            return this.$route.params.caseID
        },
        nodeID() {
            return this.$route.params.nodeID
        },
        panelID() {
            return this.$route.params.panelID
        },
        readOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        returnURL() {
            return `/library/${this.caseID}/nodes/${this.nodeID}/`
        },
        activeChoices() {
            return this.panel.choices.filter(p => !p.is_deleted)
        },
    },
    watch: {
        // Watch for changes in the custom form HTML and update the panel's configured
        // choices to keep the two in sync.
        formHtmlContent: function (newValue, oldValue) {
            let vm = this
            if (!this.formIsInit) {
                return
            }
            if (!newValue) {
                // The editor is blank, so make sure every input is in the deleted state.
                this.panel.choices.forEach(function (choice) {
                    vm.$set(choice, "is_deleted", true)
                })
            }
            // Compile a list of unique IDs for all inputs found in the form.
            let inputs = document.querySelectorAll("[data-block-type='input']")
            let inputFormIds = []
            inputs.forEach(function (el) {
                inputFormIds.push(el.getAttribute("data-block-id"))
            })

            // Loop through all the panel's choices, and set inputs to deleted
            // if they're not found in the form anymore. Un-delete them if they're found
            // in the form (this handles actions like undo-redo).
            this.panel.choices.forEach(function (choice) {
                if (inputFormIds.includes(choice.form_block_id)) {
                    choice.is_deleted = false
                    vm.updateChoiceAttributes(choice)
                } else {
                    vm.$set(choice, "is_deleted", true)
                }
            })
        },
    },
    methods: {
        // Fetch initial data for the view.
        fetchData() {
            let vm = this
            this.$store.dispatch("addLoad")
            http.get("/api/panels/" + this.panelID + "/")
                .then(function (response) {
                    vm.panel = response.data
                    if (vm.panel.elements.length) {
                        vm.formHtmlContent = vm.panel.elements[0].content
                        vm.$nextTick(() => {
                            vm.panel.choices.forEach((choice) => {
                                vm.updateChoiceAttributes(choice)
                            })
                            // Un-disable all inputs. Previously, inputs were saved as disabled in
                            // the form structure (html), so make sure all inputs are enabled as some
                            // browsers may prevent clicking on disabled inputs.
                            let inputs = document.querySelectorAll("[data-block-type='input']")
                            inputs.forEach(function (el) {
                                el.removeAttribute("disabled")
                            })
                            vm.formIsInit = true
                        })
                    } else {
                        vm.formIsInit = true
                    }
                    // Open the editor if at least one hotspot exists.
                    if (vm.panel.choices.length) {
                        vm.showEditor = true
                    }
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
            this.$store.dispatch("addLoad")
            this.$store.dispatch("fetchCase", this.caseID).then(function () {
                vm.$store.dispatch("removeLoad")
            })
        },
        // Save the panel and choice (form input) configuration.
        savePanel() {
            this.editorLoading = true

            // Set any blank scores to zero.
            this.panel.choices.filter((choice) => !choice.score).forEach((choice) => (choice.score = 0))

            this.panel.elements = [
                {
                    element_type: "text",
                    content: this.formHtmlContent,
                },
            ]

            let vm = this
            http.put("/api/panels/" + this.panel.id + "/", this.panel)
                .then(function (response) {
                    vm.panel = response.data
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Form saved.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.editorLoading = false
                })
        },
        // Create and persist a new form input.
        async createChoice(newBlock) {
            let newChoice = {
                form_block_id: newBlock.blockId,
                text: "New Input",
                placeholder: "New Input",
                message: "",
                score: 0,
                hint_message: "",
                correct_values: [""],
                accepted_value_type: "text",
                marker_width: 175,
                show_text_on_hover: false,
                accepted_value_options: [],
            }
            if (newBlock.copiedBlockId) {
                // The input was copied and pasted so copy the configured properties.
                let copiedChoice = this.panel.choices.find((c) => c.form_block_id == newBlock.copiedBlockId)
                if (copiedChoice) {
                    newChoice.text = copiedChoice.text
                    newChoice.placeholder = copiedChoice.placeholder
                    newChoice.message = copiedChoice.message
                    newChoice.score = copiedChoice.score
                    newChoice.hint_message = copiedChoice.hint_message
                    newChoice.correct_values = copiedChoice.correct_values
                    newChoice.accepted_value_type = copiedChoice.accepted_value_type
                    newChoice.marker_width = copiedChoice.marker_width
                    newChoice.show_text_on_hover = copiedChoice.show_text_on_hover
                    newChoice.accepted_value_options = copiedChoice.accepted_value_options
                }
            }
            this.panel.choices.push(newChoice)
            // Select the input in the menu after the next render.
            this.$nextTick(function () {
                this.selectChoice(newChoice)
                this.updateChoicePlaceholder(newChoice)
            })
        },
        // Shift a form input's order in the editor sidebar by an increment.
        shiftChoicePosition(index, increment) {
            // Don't do anything if the item is already at the start or end of the array.
            if ((index == 0 && increment == -1) || (index + 1 >= this.activeChoices.length && increment == 1)) {
                return
            }

            // Find the index of the item in the 'real' list of choices.
            let realIndex = this.panel.choices.findIndex(c => c.form_block_id === this.activeChoices[index].form_block_id)

            // Find the position of the nearest item that isn't deleted.
            let adjacentActiveItem = this.activeChoices[index + increment]
            let activeItemIndex = this.panel.choices.findIndex(c => c.form_block_id === adjacentActiveItem.form_block_id)

            // Remove the choice from the choice array.
            let choice = this.panel.choices.splice(realIndex, 1)[0]

            // Put it back at the new position.
            this.panel.choices.splice(activeItemIndex, 0, choice)

            // Make sure the choice is selected and scroll it into view.
            this.selectChoiceById(choice.form_block_id)
        },
        // Remove an input.
        removeChoice(choice) {
            this.$set(choice, "is_deleted", true)
            let block = document.querySelector(`[data-block-id="${choice.form_block_id}"]`)
            block.remove()
        },
        // Select a form input by its unique block ID.
        selectChoiceById(formBlockId, showEditor = true, scrollToItem = true) {
            let selectedChoice = this.panel.choices.find((choice) => choice.form_block_id == formBlockId)
            this.selectChoice(selectedChoice, showEditor, scrollToItem)
        },
        // Select a form input, and optionally open the editor sidebar and scroll to it.
        selectChoice(choice, showEditor = true, scrollToItem = true) {
            if (showEditor) {
                this.showEditor = true
            }
            if (choice.form_block_id == this.selectedChoice.form_block_id) {
                // Don't do anything if the choice is already selected.
                return
            }
            this.selectedChoice = choice
            // Scroll to the input.
            if (scrollToItem) {
                this.scrollToChoiceInMenu(choice)
            }
            this.highlightInputElement(choice)
        },
        // Scroll to a choice item in the sidebar menu.
        scrollToChoiceInMenu(choice) {
            this.$nextTick(function () {
                let choiceElement = document.getElementById("edit-choice" + choice.form_block_id)
                choiceElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                })
            })
        },
        // Select an input by clicking on it in the editor sidebar.
        selectChoiceFromMenu(choice, event) {
            // Don't do anything if the clone button was clicked.
            let clickedInClone = Boolean(event.target.closest("[data-function='clone-btn']"))
            if (clickedInClone) {
                return
            }
            // Select the choice but don't scroll to it to prevent menu from jerking around.
            this.selectChoice(choice, true, false)
        },
        // Add highlighted styling to a given form input.
        highlightInputElement(choice) {
            // Remove highlighted classes from the selected input.
            let inputs = document.querySelectorAll(".custom-form-input-focussed")
            inputs.forEach(function (el) {
                el.classList.remove("custom-form-input-focussed")
            })
            // Highlight the selected input.
            document
                .querySelector(`[data-block-id="${choice.form_block_id}"]`)
                .classList.add("custom-form-input-focussed")
        },
        // Open the editor sidebar.
        openEditor() {
            this.showEditor = true
        },
        // Close the node editor sidebar.
        closeEditor(event = null) {
            if (!event) {
                this.showEditor = false
                this.selectedChoiceIndex = null
                return
            }

            // Find out whether the click originated from an area where the editor should stay open.
            let clickedInMenu = Boolean(event.target.closest("#editor-menu"))
            let clickedInHotspot = Boolean(event.target.closest(".draggable"))
            let textEditor = Boolean(event.target.closest("#custom-form-text"))
            let saveBtn = Boolean(event.target.closest("#save-btn"))

            if (this.showEditor && !clickedInMenu && !clickedInHotspot && !textEditor && !saveBtn) {
                this.showEditor = false
                this.selectedChoiceIndex = null
            }
        },
        updateChoicePlaceholder(choice) {
            document
                .querySelector(`[data-block-id="${choice.form_block_id}"]`)
                .setAttribute("placeholder", choice.placeholder)
        },
        updateChoiceWidth(choice) {
            document.querySelector(`[data-block-id="${choice.form_block_id}"]`).style.width = choice.marker_width + "px"
        },
        // Kind of hacky - make textarea input-types taller to look more like textarea inputs. In the future,
        // we could replace the elements themselves with the correct type (i.e. textarea, select).
        updateChoiceValueType(choice) {
            let elementHeight = choice.accepted_value_type == "textarea" ? "60px" : ""
            let element = document.querySelector(`[data-block-id="${choice.form_block_id}"]`)
            element.style.height = elementHeight
            element.style["margin-top"] = "8px"
            element.style["margin-bottom"] = "8px"
        },
        // Update all attributes on a form input representing a choice.
        updateChoiceAttributes(choice) {
            this.updateChoicePlaceholder(choice)
            this.updateChoiceWidth(choice)
            this.updateChoiceValueType(choice)
        },
        // Add a select-option to a choice.
        addChoiceOption(choice) {
            let optionsCount = choice.accepted_value_options.push("")
            this.selectedOptionIndex = optionsCount - 1
            this.$nextTick(() => {
                // Bring the new input into focus.
                document.getElementById("choice-" + choice.id + "-option-" + this.selectedOptionIndex).focus()
            })
        },
        // Select an option for editing, within a choice.
        editChoiceOption(index) {
            this.selectedOptionIndex = index
        },
        // Remove a select-option from a choice.
        removeChoiceOption(choice, index) {
            choice.accepted_value_options.splice(index, 1)
        },
        // Cancel the edit state for select-options.
        closeOptionEdit() {
            this.selectedOptionIndex = null
        },
        // Returns true if the provided choice & index represent the select-option currently being edited.
        optionIsEdit(choice, index) {
            return this.selectedChoice.form_block_id === choice.form_block_id && index === this.selectedOptionIndex
        },
        addCorrectValueVariation(choice) {
            choice.correct_values.push("")
        },
        removeCorrectValueVariation(choice, index) {
            choice.correct_values.splice(index, 1)
        },
        returnToEditor() {
            if (this.previousRoute && ["PreviewCaseSession", "CaseNodeMap"].includes(this.previousRoute.name)) {
                this.$router.push(this.previousRoute)
            } else {
                this.$router.push(this.returnURL)
            }
        },
        toggleEditorDisplay() {
            this.showEditor = !this.showEditor
        },
    },
    mounted() {
        this.fetchData()
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            // Store the previous route so that we know where to route the user
            // when they press the 'back to editor' button.
            vm.previousRoute = from
        })
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.editor-container {
    width: 100%;
    @media (min-width: $breakpoint-xl) {
        width: 900px;
    }
    @media (min-width: $breakpoint-xxl) {
        width: 930px;
    }
    @media (min-width: $breakpoint-3xl) {
        width: 1000px;
    }
}

.choice-focussed {
    border-color: $primary !important;
    border-width: 1px;
}

.radius-range {
    max-width: 500px;
    min-width: 185px;
}

.radius-range-input {
    max-width: 70px;
}

.right-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1030;
    width: 420px;
    max-width: 100%;
    height: 100%;
    overflow-y: auto;
    outline: 0;
    @media (min-width: 1480px) {
        width: 460px;
    }
}

.right-sidebar-content {
    margin-left: 0px;
    margin-top: 0px;
    margin-bottom: 0px;
    height: 100%;
}

.right-sidebar-content-block {
    padding: 16px;
}

.right-sidebar-card-body {
    padding: 16px;
}

.value-option {
    padding-bottom: 2px;
}

.correct-value-input {
    width: 180px;
}

.sidebar-editor-block {
    border: 1px solid $light;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

.custom-form-input-focussed {
    border-color: $primary;
    border-width: 1.5px;
}

#custom-form-text {
    input {
        cursor: pointer !important;
    }
}
</style>